import React from "react";
import { Button, InputNumber, Popconfirm, Select, Space, Tag, Tooltip } from "antd";
import {
    ArrowRightOutlined,
    DeleteOutlined,
    DisconnectOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    SwapOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import {
    BATCH_ACTION_TEXT,
    BOOKING_STATUS_TEXT,
    CONTRACT_KIND_TEXT,
    CONTRACT_STATUS_TEXT,
    CREDIT_SOURCE_TYPE_TEXT,
    CREDIT_STATUS_TEXT,
    FLIGHT_OP_STATUS_TEXT,
    FUEL_PREVIEW_STATUS_TEXT,
    INVOICE_STATUS_TEXT,
    INVOICE_TYPE_TEXT,
    PAYMENT_SOURCE_TYPE_TEXT,
    RECEIVABLE_EXCEPTION_TEXT,
    RECEIVABLE_STATUS_TEXT,
    RECEIVABLE_TYPE_TEXT,
    RECEIVABLE_WARNING_TEXT,
    ROUTE_STATUS_OPTIONS,
    ROUTE_STATUS_TEXT,
    isReceivableRebindable,
} from "./salesRevenueConstants";
import {
    SectionTag,
    formatDate,
    formatDateTime,
    formatUtcDate,
    formatUtcDateTime,
    formatLegSegment,
    formatMoney,
    formatPercent,
    formatRange,
    getOutstandingAmount,
    isContractPastEndDate,
    getReceivableProgress,
    getToneClass,
    sortFlightLegs,
} from "./salesRevenueHelpers";

export const createSalesRevenueColumns = (ctx) => {
    const {
        canWriteContract,
        canWriteReceivable,
        canWritePayment,
        canWriteInvoice,
        canGenerateFuel,
        currencyOptions,
        invoiceDetail,
        batchGenerateResult,
        fuelCalcResult,
        openContractDetail,
        handleContractStatusChange,
        openEditRoutePriceModal,
        handleDeleteRoutePrice,
        openEditFuelTierModal,
        handleToggleFuelTierStatus,
        updateFuelTierRow,
        removeFuelTierRow,
        handleUnbindContract,
        handleReceivableRecalculate,
        openExtraFeeModal,
        handleGenerateBaseReceivable,
        openFuelCalcModal,
        handleReceivableRematch,
        openRebindContractModal,
        openAdjustAmountModal,
        openInvoiceDetail,
        openPaymentModal,
        handleVoidInvoice,
        detailContractKind,
        bookingDetailKind,
        openAdHocAmountModal,
    } = ctx;

    const contractColumns = [
        {
            title: "合同编号",
            dataIndex: "contract_no",
            key: "contract_no",
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <Button type="link" className="sales-revenue-link-button" onClick={() => openContractDetail(record.id)}>
                        {record.contract_no}
                    </Button>
                    <div className="sales-revenue-cell-sub">{CONTRACT_KIND_TEXT[record.contract_kind] || "阶梯合同"}</div>
                </div>
            ),
        },
        {
            title: "客户",
            dataIndex: "customer_name",
            key: "customer_name",
            ellipsis: true,
        },
        {
            title: "有效期",
            key: "date_range",
            render: (_, record) =>
                !record.start_date && !record.end_date ? "永久有效" : formatRange(record.start_date, record.end_date),
        },
        {
            title: "币种",
            dataIndex: "currency",
            key: "currency",
            width: 80,
        },
        {
            title: "基准油价 ($/MT)",
            dataIndex: "base_fuel_price_per_mt",
            key: "base_fuel_price_per_mt",
            align: "right",
            render: (value) => <span className="sales-revenue-money">{formatMoney(value)}</span>,
        },
        {
            title: "航线数",
            dataIndex: "route_count",
            key: "route_count",
            align: "center",
            width: 90,
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (value, record) => (
                <Space size={4} wrap>
                    <SectionTag tone={getToneClass("contract-status", value)}>{CONTRACT_STATUS_TEXT[value] || value}</SectionTag>
                    {isContractPastEndDate(record) ? <SectionTag tone="gold">已过失效日</SectionTag> : null}
                </Space>
            ),
        },
        {
            title: "操作",
            key: "actions",
            width: 140,
            render: (_, record) => (
                <Space size={4} wrap>
                    {canWriteContract ? (
                        <Button type="link" size="small" onClick={() => openContractDetail(record.id)}>
                            编辑
                        </Button>
                    ) : null}
                    {canWriteContract && record.status !== "TERMINATED" ? (
                        <Button type="link" size="small" danger={record.status === "ACTIVE"} onClick={() => handleContractStatusChange(record)}>
                            {record.status === "ACTIVE" ? "终止" : record.status === "EXPIRED" ? "重新启用" : "启用"}
                        </Button>
                    ) : null}
                </Space>
            ),
        },
    ];

    const priceColumns = [
        {
            title: "合同航线",
            key: "route",
            render: (_, record) => `${record.origin_airport} → ${record.destination_airport}`,
        },
        {
            title: "机型",
            dataIndex: "aircraft_type",
            key: "aircraft_type",
            render: (value) => value || "不限机型",
        },
        {
            title: "不限飞法",
            key: "undifferentiated_unit_price",
            align: "right",
            render: (_, record) =>
                record.undifferentiated_status === "ACTIVE" && record.undifferentiated_unit_price != null ? (
                    <span className="sales-revenue-money">
                        {formatMoney(record.undifferentiated_unit_price)} {record.currency}
                    </span>
                ) : (
                    "—"
                ),
        },
        {
            title: "直飞",
            key: "direct_unit_price",
            align: "right",
            render: (_, record) =>
                record.direct_status === "ACTIVE" && record.direct_unit_price != null ? (
                    <span className="sales-revenue-money">
                        {formatMoney(record.direct_unit_price)} {record.currency}
                    </span>
                ) : (
                    "—"
                ),
        },
        {
            title: "经停",
            key: "with_stop_unit_price",
            align: "right",
            render: (_, record) =>
                record.with_stop_status === "ACTIVE" && record.with_stop_unit_price != null ? (
                    <span className="sales-revenue-money">
                        {formatMoney(record.with_stop_unit_price)} {record.currency}
                    </span>
                ) : (
                    "—"
                ),
        },
        {
            title: "说明",
            key: "pricing_note",
            width: 220,
            render: (_, record) => {
                const notes = [record.pricing_note, record.currency_differs_from_contract ? "币种和合同不一致" : null].filter(Boolean);
                return notes.length ? notes.join("；") : "—";
            },
        },
        {
            title: "生效日期",
            dataIndex: "effective_date",
            key: "effective_date",
            width: 120,
            render: formatDate,
        },
        {
            title: "失效日期",
            dataIndex: "expiry_date",
            key: "expiry_date",
            width: 120,
            render: formatDate,
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 90,
            render: (value) => (
                <SectionTag tone={value === "ACTIVE" ? "green" : "gray"}>{ROUTE_STATUS_TEXT[value] || value || "—"}</SectionTag>
            ),
        },
        {
            title: "操作",
            key: "actions",
            width: 160,
            render: (_, record) =>
                canWriteContract ? (
                    <Space size={4}>
                        <Button type="link" size="small" onClick={() => openEditRoutePriceModal(record)}>
                            编辑
                        </Button>
                        {record.status === "ACTIVE" ? (
                            <Button type="link" size="small" danger onClick={() => handleDeleteRoutePrice(record)}>
                                停用
                            </Button>
                        ) : null}
                    </Space>
                ) : (
                    "—"
                ),
        },
    ];

    const fuelTierColumns = [
        {
            title: "偏差下限 ($/MT)",
            dataIndex: "diff_min",
            key: "diff_min",
            render: (value) => <span className="sales-revenue-money">{formatMoney(value)}</span>,
        },
        {
            title: "偏差上限 ($/MT)",
            dataIndex: "diff_max",
            key: "diff_max",
            render: (value) => <span className="sales-revenue-money">{formatMoney(value)}</span>,
        },
        {
            title: "调整金额",
            dataIndex: "adjustment_amount",
            key: "adjustment_amount",
            align: "right",
            render: (value, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(value)} {record.currency}
                </span>
            ),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (value) => <SectionTag tone={value === "ACTIVE" ? "green" : "gray"}>{value === "ACTIVE" ? "启用" : "停用"}</SectionTag>,
        },
        {
            title: "操作",
            key: "actions",
            width: 140,
            render: (_, record) =>
                canWriteContract ? (
                    <Space size={4} wrap>
                        <Button type="link" size="small" onClick={() => openEditFuelTierModal(record)}>
                            编辑
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            danger={record.status === "ACTIVE"}
                            onClick={() => handleToggleFuelTierStatus(record)}
                        >
                            {record.status === "ACTIVE" ? "停用" : "启用"}
                        </Button>
                    </Space>
                ) : (
                    "—"
                ),
        },
    ];

    const fuelTierBatchColumns = [
        {
            title: "偏差下限 ($/MT)",
            key: "diff_min",
            width: 150,
            render: (_, record) => (
                <InputNumber
                    className="sales-revenue-full-width"
                    precision={2}
                    value={record.diff_min}
                    onChange={(value) => updateFuelTierRow(record.key, "diff_min", value)}
                    placeholder="下限"
                />
            ),
        },
        {
            title: "偏差上限 ($/MT)",
            key: "diff_max",
            width: 150,
            render: (_, record) => (
                <InputNumber
                    className="sales-revenue-full-width"
                    precision={2}
                    value={record.diff_max}
                    onChange={(value) => updateFuelTierRow(record.key, "diff_max", value)}
                    placeholder="上限"
                />
            ),
        },
        {
            title: "调整金额",
            key: "adjustment_amount",
            width: 150,
            render: (_, record) => (
                <InputNumber
                    className="sales-revenue-full-width"
                    precision={2}
                    value={record.adjustment_amount}
                    onChange={(value) => updateFuelTierRow(record.key, "adjustment_amount", value)}
                    placeholder="每班附加费（减价填负数）"
                />
            ),
        },
        {
            title: "币种",
            key: "currency",
            width: 110,
            render: (_, record) => (
                <Select
                    className="sales-revenue-full-width"
                    options={currencyOptions}
                    value={record.currency}
                    onChange={(value) => updateFuelTierRow(record.key, "currency", value)}
                />
            ),
        },
        {
            title: "状态",
            key: "status",
            width: 110,
            render: (_, record) => (
                <Select
                    className="sales-revenue-full-width"
                    options={ROUTE_STATUS_OPTIONS.filter((item) => item.value)}
                    value={record.status}
                    onChange={(value) => updateFuelTierRow(record.key, "status", value)}
                />
            ),
        },
        {
            title: "",
            key: "actions",
            width: 56,
            render: (_, record) => (
                <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeFuelTierRow(record.key)}
                />
            ),
        },
    ];

    const prepaymentColumns = [
        {
            title: "收款日期",
            dataIndex: "received_date",
            key: "received_date",
            width: 120,
            render: formatDate,
        },
        {
            title: "预付总额",
            dataIndex: "amount",
            key: "amount",
            align: "right",
            render: (value, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(value)} {record.currency}
                </span>
            ),
        },
        {
            title: "已抵扣",
            dataIndex: "used_amount",
            key: "used_amount",
            align: "right",
            render: (value, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(value)} {record.currency}
                </span>
            ),
        },
        {
            title: "剩余可用",
            dataIndex: "remaining_amount",
            key: "remaining_amount",
            align: "right",
            render: (value, record) => (
                <span className="sales-revenue-money sales-revenue-money-success">
                    {formatMoney(value)} {record.currency}
                </span>
            ),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (value) => <SectionTag tone={getToneClass("credit-status", value)}>{CREDIT_STATUS_TEXT[value] || value}</SectionTag>,
        },
    ];

    const contractBookingColumns = [
        {
            title: "CB 订单",
            key: "booking_no",
            width: 200,
            sorter: (a, b) => (a.booking_no || "").localeCompare(b.booking_no || ""),
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{record.booking_no}</div>
                    <div className="sales-revenue-cell-sub">{record.flight_no || "未排航班"}</div>
                </div>
            ),
        },
        {
            title: "航线/描述",
            key: "route_description",
            sorter: (a, b) => (a.route_description || "").localeCompare(b.route_description || ""),
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{record.route_description || "—"}</div>
                    {record.flight_count ? <div className="sales-revenue-cell-sub">共 {record.flight_count} 段</div> : null}
                </div>
            ),
        },
        {
            title: "期间",
            dataIndex: "period",
            key: "period",
            width: 110,
            sorter: (a, b) => (a.period || "").localeCompare(b.period || ""),
        },
        {
            title: "基础运价应收",
            key: "base_fare",
            width: 200,
            render: (_, record) => {
                if (record.receivable_count === 0) {
                    return <span className="sales-revenue-cell-sub">未生成应收</span>;
                }
                return (
                    <div className="sales-revenue-cell-stack">
                        <div className="sales-revenue-cell-main sales-revenue-money">
                            {record.base_fare_amount == null ? "—" : `${formatMoney(record.base_fare_amount)} ${record.currency || ""}`}
                        </div>
                        <Space size={4} wrap>
                            {record.base_fare_status ? (
                                <SectionTag tone={getToneClass("receivable-status", record.base_fare_status)}>
                                    {RECEIVABLE_STATUS_TEXT[record.base_fare_status] || record.base_fare_status}
                                </SectionTag>
                            ) : null}
                            {record.has_exception ? (
                                <Tooltip title={record.exception_message || "存在异常应收"}>
                                    <Tag color="error">异常</Tag>
                                </Tooltip>
                            ) : null}
                            {record.has_warning ? (
                                <Tooltip title={record.warning_message || "存在提醒"}>
                                    <Tag color="warning">提醒</Tag>
                                </Tooltip>
                            ) : null}
                        </Space>
                    </div>
                );
            },
        },
        {
            title: "订单状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (value) => <Tag color={value === "ACTIVE" ? "green" : value === "CANCELLED" ? "red" : "default"}>{BOOKING_STATUS_TEXT[value] || value}</Tag>,
        },
        {
            title: "操作",
            key: "actions",
            width: detailContractKind === "AD_HOC" ? 190 : 110,
            render: (_, record) => {
                if (!canWriteReceivable) {
                    return <span className="sales-revenue-cell-muted">—</span>;
                }
                const adHocButton =
                    detailContractKind === "AD_HOC" ? (
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openAdHocAmountModal(record)}>
                            手填燃油
                        </Button>
                    ) : null;
                // 仅按"非作废应收"判定能否解绑，与后端 _ensure_booking_unbindable 同口径；
                // receivable_count 含 CANCELLED 会让应收全部作废后的订单永久置灰，运营走不通流程
                const activeReceivables = record.active_receivable_count ?? record.receivable_count ?? 0;
                if (record.linked_to_transport_booking) {
                    return (
                        <Space size={0}>
                            {adHocButton}
                            <Tooltip title="该订单已挂在订舱上，请到订舱取消挂接或换挂">
                                <Button type="link" size="small" icon={<DisconnectOutlined />} disabled>
                                    解绑
                                </Button>
                            </Tooltip>
                        </Space>
                    );
                }
                if (activeReceivables > 0) {
                    return (
                        <Space size={0}>
                            {adHocButton}
                            <Tooltip title="已生成应收，不能解绑；如需更换合同请在应收列表改绑">
                                <Button type="link" size="small" icon={<DisconnectOutlined />} disabled>
                                    解绑
                                </Button>
                            </Tooltip>
                        </Space>
                    );
                }
                return (
                    <Space size={0}>
                        {adHocButton}
                        <Popconfirm
                            title="确认解绑该订单的合同？"
                            description="解绑后订单上的合同会清空，客户保留不变。"
                            okText="解绑"
                            cancelText="取消"
                            onConfirm={() => handleUnbindContract(record)}
                        >
                            <Button type="link" size="small" danger icon={<DisconnectOutlined />}>
                                解绑
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const bookingColumns = [
        {
            title: "CB订单 / 航线",
            key: "booking",
            width: 240,
            render: (_, group) => {
                const legs = sortFlightLegs(group.flight_legs_snapshot);
                return (
                    <div className="sales-revenue-cell-stack">
                        <div className="sales-revenue-cell-main">{group.booking_no || "—"}</div>
                        <div className="sales-revenue-cell-sub">
                            {group.route_display || "—"}
                            {group.aircraft_type ? ` · ${group.aircraft_type}` : ""}
                        </div>
                        {group.linked_to_transport_booking ? (
                            <div className="sales-revenue-cell-sub">订舱 {group.demand_booking_no || "已挂"}</div>
                        ) : null}
                        {legs.length ? (
                            <div className="sales-revenue-cell-sub">
                                {formatLegSegment(legs[0])}
                                {legs.length > 1 ? ` 等 ${legs.length} 段` : ""}
                            </div>
                        ) : null}
                    </div>
                );
            },
        },
        {
            title: "计划起飞 (UTC)",
            key: "std",
            width: 130,
            render: (_, group) => {
                const firstLeg = sortFlightLegs(group.flight_legs_snapshot)[0];
                const stdValue = firstLeg?.std || group.flight_date;
                return (
                    <div className="sales-revenue-cell-stack">
                        <div className="sales-revenue-cell-main">{formatUtcDate(stdValue)}</div>
                        <div className="sales-revenue-cell-sub">首段计划 · UTC</div>
                    </div>
                );
            },
        },
        {
            title: "客户 / 合同",
            key: "customer",
            width: 200,
            render: (_, group) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{group.customer_name || "—"}</div>
                    <div className="sales-revenue-cell-sub">{group.contract_no || "未归属合同"}</div>
                </div>
            ),
        },
        {
            title: "应收合计",
            key: "total",
            width: 180,
            render: (_, group) => {
                const list = group.receivables || [];
                const cancelledCount = list.filter((item) => item.status === "CANCELLED").length;
                return (
                    <div className="sales-revenue-cell-stack">
                        <div className="sales-revenue-money sales-revenue-cell-main">
                            {formatMoney(group.summary?.total_amount)} {group.summary?.currency}
                        </div>
                        <div className="sales-revenue-cell-sub">
                            {list.length} 条应收
                            {cancelledCount > 0 ? `（含 ${cancelledCount} 条已取消）` : ""}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "收款进度",
            key: "payment_progress",
            width: 220,
            render: (_, group) => {
                const total = Number(group.summary?.total_amount || 0);
                const paid = Number(group.summary?.paid_amount || 0);
                const percent = total > 0 ? (paid / total) * 100 : 0;
                return (
                    <div className="sales-revenue-progress-block">
                        <div className="sales-revenue-progress-meta">
                            <span className="sales-revenue-money">
                                {formatMoney(paid)} / {formatMoney(total)}
                            </span>
                            <span>{formatPercent(percent)}</span>
                        </div>
                        <div className="sales-revenue-progress-track">
                            <div
                                className={`sales-revenue-progress-fill sales-revenue-progress-fill-${getToneClass("receivable-status", group.summary?.status)}`}
                                style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
                            />
                        </div>
                        <div className="sales-revenue-cell-sub">
                            未收 {formatMoney(group.summary?.unpaid_amount)} {group.summary?.currency}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "汇总状态",
            key: "summary_status",
            width: 160,
            render: (_, group) => (
                <div className="sales-revenue-risk-stack">
                    <SectionTag tone={getToneClass("receivable-status", group.summary?.status)}>
                        {RECEIVABLE_STATUS_TEXT[group.summary?.status] || group.summary?.status}
                    </SectionTag>
                    {group.summary?.has_exception ? <SectionTag tone="red">含异常</SectionTag> : null}
                </div>
            ),
        },
        {
            title: "操作",
            key: "actions",
            width: 200,
            render: (_, group) => {
                const bookingRecord = {
                    charter_booking_id: group.booking_id,
                    booking_no: group.booking_no,
                    contract_id: group.contract_id,
                    currency: group.summary?.currency || "USD",
                    flight_date_snapshot: group.flight_date,
                    billing_timezone: "UTC",
                };
                return (
                    <Space size={4} wrap onClick={(event) => event.stopPropagation()}>
                        {canWriteReceivable ? (
                            <Button type="link" size="small" icon={<SyncOutlined />} onClick={() => handleReceivableRecalculate(bookingRecord)}>
                                联动重算
                            </Button>
                        ) : null}
                        {canWriteReceivable ? (
                            <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => openExtraFeeModal(bookingRecord)}>
                                新增费用
                            </Button>
                        ) : null}
                    </Space>
                );
            },
        },
    ];

    const bookingReceivableDetailColumns = [
        {
            title: "类型",
            key: "type",
            width: 150,
            render: (_, record) => {
                if (record._placeholder) {
                    return (
                        <span className="sales-revenue-cell-muted">
                            {RECEIVABLE_TYPE_TEXT[record.type] || record.type} · 尚未生成
                        </span>
                    );
                }
                return (
                    <span>
                        {RECEIVABLE_TYPE_TEXT[record.type] || record.type}
                        {record.extra_fee_type ? ` · ${record.extra_fee_type}` : ""}
                    </span>
                );
            },
        },
        {
            title: "应收编号",
            dataIndex: "receivable_no",
            key: "receivable_no",
            width: 190,
            render: (value, record) => (record._placeholder ? "—" : value),
        },
        {
            title: "应收 / 已收",
            key: "amount",
            width: 200,
            render: (_, record) => {
                if (record._placeholder) {
                    return <span className="sales-revenue-cell-muted">—</span>;
                }
                const progress = getReceivableProgress(record);
                return (
                    <div className="sales-revenue-cell-stack">
                        <div className="sales-revenue-money sales-revenue-cell-main">
                            {formatMoney(record.amount)} {record.currency}
                        </div>
                        <div className="sales-revenue-cell-sub">
                            已收 {formatMoney(progress.paidAmount)} · 未收 {formatMoney(progress.remainingAmount)}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "状态",
            key: "status",
            width: 190,
            render: (_, record) => {
                if (record._placeholder) {
                    return <SectionTag tone="default">缺{record.type === "BASE_FARE" ? "基础运价" : "燃油附加费"}</SectionTag>;
                }
                return (
                    <div className="sales-revenue-risk-stack">
                        <SectionTag tone={getToneClass("receivable-status", record.status)}>
                            {RECEIVABLE_STATUS_TEXT[record.status] || record.status}
                        </SectionTag>
                        {record.exception_code ? (
                            <SectionTag tone="red">{RECEIVABLE_EXCEPTION_TEXT[record.exception_code] || record.exception_code}</SectionTag>
                        ) : null}
                        {!record.exception_code && record.warning_code ? (
                            <Tooltip title={record.warning_message || ""}>
                                <SectionTag tone="gold">提醒 · {RECEIVABLE_WARNING_TEXT[record.warning_code] || record.warning_code}</SectionTag>
                            </Tooltip>
                        ) : null}
                        {record.manual_adjust_reason ? (
                            <Tooltip title={`调整原因：${record.manual_adjust_reason}`}>
                                <SectionTag tone="purple">已手工调整</SectionTag>
                            </Tooltip>
                        ) : null}
                    </div>
                );
            },
        },
        {
            title: "操作",
            key: "actions",
            width: 280,
            render: (_, record) => {
                if (record._placeholder) {
                    if (record.type === "BASE_FARE") {
                        return canWriteReceivable ? (
                            <Button type="link" size="small" icon={<SyncOutlined />} onClick={() => handleGenerateBaseReceivable(record)}>
                                生成基础运价
                            </Button>
                        ) : (
                            <span className="sales-revenue-cell-muted">待生成</span>
                        );
                    }
                    if (bookingDetailKind === "AD_HOC") {
                        return <span className="sales-revenue-cell-muted">请在下方手填</span>;
                    }
                    return canGenerateFuel ? (
                        <Button type="link" size="small" icon={<ArrowRightOutlined />} onClick={openFuelCalcModal}>
                            去计算燃油
                        </Button>
                    ) : (
                        <span className="sales-revenue-cell-muted">待计算</span>
                    );
                }
                // 已取消的应收只保留「详情」查看，不再提供重匹配 / 改绑等写操作
                const isCancelled = record.status === "CANCELLED";
                return (
                    <Space size={4} wrap>
                        {canWriteReceivable && !isCancelled && record.type === "BASE_FARE" ? (
                            <Button type="link" size="small" icon={<SyncOutlined />} onClick={() => handleReceivableRematch(record)}>
                                重匹配
                            </Button>
                        ) : null}
                        {canWriteReceivable && !isCancelled && record.linked_to_transport_booking ? (
                            <Tooltip title="该订单已挂在订舱上，请到订舱取消挂接或换挂">
                                <Button type="link" size="small" icon={<SwapOutlined />} disabled>
                                    改绑合同
                                </Button>
                            </Tooltip>
                        ) : null}
                        {canWriteReceivable && !isCancelled && isReceivableRebindable(record) ? (
                            <Button type="link" size="small" icon={<SwapOutlined />} onClick={() => openRebindContractModal(record)}>
                                改绑合同
                            </Button>
                        ) : null}
                        {canWriteReceivable && !isCancelled && !record.is_locked ? (
                            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openAdjustAmountModal(record)}>
                                调整金额
                            </Button>
                        ) : null}
                    </Space>
                );
            },
        },
    ];

    const invoiceColumns = [
        {
            title: "发票号",
            dataIndex: "invoice_no",
            key: "invoice_no",
            width: 200,
            render: (_, record) => (
                <Button type="link" className="sales-revenue-link-button" onClick={() => openInvoiceDetail(record)}>
                    {record.invoice_no}
                </Button>
            ),
        },
        {
            title: "客户 / 合同",
            key: "contract",
            width: 240,
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{record.customer_name || "—"}</div>
                    <div className="sales-revenue-cell-sub">{record.contract_no || "跨合同 / 未归属主合同"}</div>
                </div>
            ),
        },
        {
            title: "账单范围",
            key: "bookings",
            width: 240,
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{record.linked_booking_nos?.slice(0, 2).join(" / ") || "—"}</div>
                    <div className="sales-revenue-cell-sub">
                        关联 {record.receivable_count || 0} 条应收 / {record.linked_booking_count || 0} 笔订单
                    </div>
                </div>
            ),
        },
        {
            title: "发票信息",
            key: "invoice_info",
            width: 240,
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-money sales-revenue-cell-main">
                        {formatMoney(record.total_amount)} {record.currency}
                    </div>
                    <div className="sales-revenue-cell-sub">
                        {INVOICE_TYPE_TEXT[record.invoice_type] || record.invoice_type} · {formatDate(record.invoice_date)}
                    </div>
                </div>
            ),
        },
        {
            title: "收款进度",
            key: "payment_progress",
            width: 220,
            render: (_, record) => {
                const percent = Number(record.total_amount || 0) > 0 ? (Number(record.paid_amount || 0) / Number(record.total_amount || 0)) * 100 : 0;
                const outstandingAmount = getOutstandingAmount(record.total_amount, record.paid_amount);
                return (
                    <div className="sales-revenue-progress-block">
                        <div className="sales-revenue-progress-meta">
                            <span className="sales-revenue-money">
                                {formatMoney(record.paid_amount)} / {formatMoney(record.total_amount)}
                            </span>
                            <span>{formatPercent(percent)}</span>
                        </div>
                        <div className="sales-revenue-progress-track">
                            <div
                                className={`sales-revenue-progress-fill sales-revenue-progress-fill-${getToneClass("invoice-status", record.status)}`}
                                style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
                            />
                        </div>
                        <div className="sales-revenue-cell-sub">
                            未收 {formatMoney(outstandingAmount)} {record.currency}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (value) => <SectionTag tone={getToneClass("invoice-status", value)}>{INVOICE_STATUS_TEXT[value] || value}</SectionTag>,
        },
        {
            title: "操作",
            key: "actions",
            width: 220,
            render: (_, record) => (
                <Space size={4} wrap>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openInvoiceDetail(record)}>
                        详情
                    </Button>
                    {canWritePayment && record.status !== "VOIDED" && getOutstandingAmount(record.total_amount, record.paid_amount) > 0 ? (
                        <Button type="link" size="small" onClick={() => openPaymentModal(record)}>
                            录入收款
                        </Button>
                    ) : null}
                    {canWriteInvoice && Number(record.paid_amount || 0) <= 0 && record.status !== "VOIDED" ? (
                        <Button type="link" size="small" danger onClick={() => handleVoidInvoice(record)}>
                            作废
                        </Button>
                    ) : null}
                </Space>
            ),
        },
    ];

    const invoiceCandidateColumns = [
        {
            title: "CB 订单",
            dataIndex: "booking_no",
            key: "booking_no",
            width: 160,
        },
        {
            title: "应收编号",
            dataIndex: "receivable_no",
            key: "receivable_no",
            width: 200,
        },
        {
            title: "类型",
            dataIndex: "type",
            key: "type",
            width: 120,
            render: (value) => RECEIVABLE_TYPE_TEXT[value] || value,
        },
        {
            title: "计费日期",
            dataIndex: "flight_date_snapshot",
            key: "flight_date_snapshot",
            width: 120,
            render: formatDate,
        },
        {
            title: "航线",
            dataIndex: "route_display_snapshot",
            key: "route_display_snapshot",
            width: 180,
        },
        {
            title: "金额",
            key: "amount",
            width: 150,
            render: (_, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(record.amount)} {record.currency}
                </span>
            ),
        },
    ];

    const invoiceReceivableColumns = [
        {
            title: "CB 订单",
            dataIndex: "booking_no",
            key: "booking_no",
            width: 160,
        },
        {
            title: "应收编号",
            dataIndex: "receivable_no",
            key: "receivable_no",
            width: 200,
        },
        {
            title: "类型",
            dataIndex: "type",
            key: "type",
            width: 120,
            render: (value) => RECEIVABLE_TYPE_TEXT[value] || value,
        },
        {
            title: "航线",
            dataIndex: "route_display_snapshot",
            key: "route_display_snapshot",
            width: 180,
        },
        {
            title: "计费日期",
            dataIndex: "flight_date_snapshot",
            key: "flight_date_snapshot",
            width: 120,
            render: formatDate,
        },
        {
            title: "金额",
            key: "amount",
            width: 150,
            render: (_, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(record.amount)} {invoiceDetail?.currency}
                </span>
            ),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (value) => <SectionTag tone={getToneClass("receivable-status", value)}>{RECEIVABLE_STATUS_TEXT[value] || value}</SectionTag>,
        },
    ];

    const invoicePaymentColumns = [
        {
            title: "收款号",
            dataIndex: "payment_no",
            key: "payment_no",
            width: 200,
        },
        {
            title: "来源",
            key: "source_type",
            width: 180,
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{PAYMENT_SOURCE_TYPE_TEXT[record.source_type] || record.source_type}</div>
                    <div className="sales-revenue-cell-sub">{record.payment_method || "—"}</div>
                </div>
            ),
        },
        {
            title: "金额",
            key: "amount",
            width: 150,
            render: (_, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(record.amount)} {record.currency}
                </span>
            ),
        },
        {
            title: "日期",
            dataIndex: "payment_date",
            key: "payment_date",
            width: 120,
            render: formatDate,
        },
        {
            title: "抵扣明细",
            key: "credit_usages",
            render: (_, record) =>
                record.credit_usages?.length ? (
                    <div className="sales-revenue-inline-list">
                        {record.credit_usages.map((usage) => (
                            <div key={usage.id}>
                                {CREDIT_SOURCE_TYPE_TEXT[usage.source_type] || usage.source_type} · {formatMoney(usage.amount)} {invoiceDetail?.currency}
                            </div>
                        ))}
                    </div>
                ) : (
                    "—"
                ),
        },
    ];

    const paymentAllocationColumns = [
        {
            title: "收款号",
            dataIndex: "payment_no",
            key: "payment_no",
            width: 200,
        },
        {
            title: "应收编号",
            key: "receivable",
            width: 220,
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{record.receivable_no}</div>
                    <div className="sales-revenue-cell-sub">{record.booking_no || "—"}</div>
                </div>
            ),
        },
        {
            title: "分摊金额",
            key: "amount",
            width: 150,
            render: (_, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(record.amount)} {invoiceDetail?.currency}
                </span>
            ),
        },
        {
            title: "分摊顺序",
            dataIndex: "allocation_order",
            key: "allocation_order",
            width: 100,
            align: "center",
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: formatDateTime,
        },
    ];

    const receivableFlightLegColumns = [
        {
            title: "航班号",
            dataIndex: "flight_number",
            key: "flight_number",
            width: 120,
        },
        {
            title: "航段",
            key: "route",
            render: (_, record) => `${record.departure_airport || "—"} → ${record.arrival_airport || "—"}`,
        },
        {
            title: "STD (UTC)",
            dataIndex: "std",
            key: "std",
            width: 160,
            render: formatUtcDateTime,
        },
        {
            title: "Airborne (UTC)",
            dataIndex: "atd_icao",
            key: "atd_icao",
            width: 160,
            render: formatUtcDateTime,
        },
        {
            title: "机型",
            dataIndex: "aircraft_type",
            key: "aircraft_type",
            width: 120,
            render: (value) => value || "—",
        },
        {
            title: "机号",
            dataIndex: "aircraft_reg",
            key: "aircraft_reg",
            width: 120,
            render: (value) => value || "—",
        },
    ];

    const bookingFlightColumns = [
        {
            title: "航班号",
            dataIndex: "flight_number",
            key: "flight_number",
            width: 110,
            render: (value) => value || "—",
        },
        {
            title: "航段",
            key: "segment",
            width: 130,
            render: (_, record) => `${record.departure_airport || "—"} → ${record.arrival_airport || "—"}`,
        },
        {
            title: "计划起飞 STD (UTC)",
            dataIndex: "std",
            key: "std",
            width: 170,
            render: formatUtcDateTime,
        },
        {
            title: "实际 / 预计 (UTC)",
            key: "actual",
            width: 170,
            render: (_, record) => formatUtcDateTime(record.atd || record.etd) || "—",
        },
        {
            title: "机型 / 机号",
            key: "aircraft",
            width: 150,
            render: (_, record) =>
                `${record.aircraft_type || "—"}${record.aircraft_reg ? ` / ${record.aircraft_reg}` : ""}`,
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (value) => {
                const cancelled = value === "CANCELLED" || value === "PLAN_CANCELLED";
                const tone = cancelled
                    ? "red"
                    : value === "REPLACED"
                      ? "default"
                      : value === "COMPLETED"
                        ? "green"
                        : value === "DELAYED" || value === "DIVERTED"
                          ? "gold"
                          : "blue";
                return <SectionTag tone={tone}>{FLIGHT_OP_STATUS_TEXT[value] || value || "—"}</SectionTag>;
            },
        },
    ];

    const receivableEventColumns = [
        {
            title: "时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: formatDateTime,
        },
        {
            title: "事件",
            dataIndex: "event_type",
            key: "event_type",
            width: 160,
        },
        {
            title: "操作人",
            key: "operator",
            width: 140,
            render: (_, record) => record.operator_name || (record.operator_id ? `#${record.operator_id}` : "系统"),
        },
        {
            title: "说明",
            dataIndex: "description",
            key: "description",
            render: (value) => value || "—",
        },
    ];

    const batchGenerateColumns = [
        {
            title: "CB 订单",
            key: "booking",
            width: 200,
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-main">{record.booking_no}</div>
                    <div className="sales-revenue-cell-sub">{record.flight_no || "未排航班"}</div>
                </div>
            ),
        },
        {
            title: "航线",
            dataIndex: "route_display",
            key: "route_display",
            width: 180,
            render: (value) => value || "—",
        },
        {
            title: "动作",
            dataIndex: "action",
            key: "action",
            width: 140,
            render: (value) => <SectionTag tone={getToneClass("batch-action", value)}>{BATCH_ACTION_TEXT[value] || value}</SectionTag>,
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (value) => (value ? <SectionTag tone={getToneClass("receivable-status", value)}>{RECEIVABLE_STATUS_TEXT[value] || value}</SectionTag> : "—"),
        },
        {
            title: "金额",
            key: "amount",
            width: 150,
            render: (_, record) => (record.amount != null ? `${formatMoney(record.amount)} ${record.currency || ""}` : "—"),
        },
        {
            title: "结果",
            key: "message",
            width: 320,
            render: (_, record) => (
                <div className="sales-revenue-cell-stack">
                    <div className="sales-revenue-cell-message">{record.message || "—"}</div>
                    <div className="sales-revenue-cell-sub">
                        {record.exception_code
                            ? RECEIVABLE_EXCEPTION_TEXT[record.exception_code] || record.exception_code
                            : record.warning_code
                              ? `提醒 · ${RECEIVABLE_WARNING_TEXT[record.warning_code] || record.warning_code}`
                              : "—"}
                    </div>
                </div>
            ),
        },
        {
            title: "操作",
            key: "actions",
            width: 120,
            render: (_, record) =>
                canWriteReceivable &&
                batchGenerateResult?.dry_run &&
                record.booking_id &&
                !["SKIP_MISSING_CONTRACT", "CREATED", "REUSED"].includes(record.action) ? (
                    <Button type="link" size="small" onClick={() => handleGenerateBaseReceivable(record)}>
                        生成应收
                    </Button>
                ) : (
                    "—"
                ),
        },
    ];

    const fuelCalcColumns = [
        {
            title: "CB 订单",
            dataIndex: "booking_no",
            key: "booking_no",
            width: 160,
        },
        {
            title: "去程航线",
            dataIndex: "route",
            key: "route",
            width: 160,
        },
        {
            title: "航段",
            dataIndex: "route_display",
            key: "route_display",
            width: 220,
        },
        {
            title: "航班日期",
            dataIndex: "flight_date",
            key: "flight_date",
            width: 120,
            render: formatDate,
        },
        {
            title: "航班号",
            dataIndex: "flight_number",
            key: "flight_number",
            width: 120,
            render: (value) => value || "—",
        },
        {
            title: "机号",
            dataIndex: "aircraft_reg",
            key: "aircraft_reg",
            width: 120,
            render: (value) => value || "—",
        },
        {
            title: "调整金额",
            key: "adjustment_amount",
            width: 170,
            align: "right",
            render: (_, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(record.adjustment_amount)} {record.currency}
                </span>
            ),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 180,
            render: (value, record) => (
                <div className="sales-revenue-risk-stack">
                    <SectionTag tone={getToneClass("fuel-preview-status", value)}>{FUEL_PREVIEW_STATUS_TEXT[value] || value}</SectionTag>
                    {record.message ? <div className="sales-revenue-cell-sub">{record.message}</div> : null}
                </div>
            ),
        },
    ];

    const fuelCalcTierColumns = [
        {
            title: "偏差下限 ($/MT)",
            dataIndex: "diff_min",
            key: "diff_min",
            render: (value) => <span className="sales-revenue-money">{formatMoney(value)}</span>,
        },
        {
            title: "偏差上限 ($/MT)",
            dataIndex: "diff_max",
            key: "diff_max",
            render: (value) => <span className="sales-revenue-money">{formatMoney(value)}</span>,
        },
        {
            title: "调整金额 (USD/班)",
            dataIndex: "adjustment_amount",
            key: "adjustment_amount",
            align: "right",
            render: (value, record) => (
                <span className="sales-revenue-money">
                    {formatMoney(value)} {record.currency}
                </span>
            ),
        },
        {
            title: "当前匹配",
            key: "current_match",
            width: 160,
            render: (_, record) =>
                fuelCalcResult?.matched_tier?.id === record.id ? (
                    <SectionTag tone="blue">当前 {fuelCalcResult?.diff != null ? `${formatMoney(fuelCalcResult.diff)}` : "—"}</SectionTag>
                ) : (
                    "—"
                ),
        },
    ];

    return {
        contractColumns,
        priceColumns,
        fuelTierColumns,
        fuelTierBatchColumns,
        prepaymentColumns,
        contractBookingColumns,
        bookingColumns,
        bookingReceivableDetailColumns,
        invoiceColumns,
        invoiceCandidateColumns,
        invoiceReceivableColumns,
        invoicePaymentColumns,
        paymentAllocationColumns,
        receivableFlightLegColumns,
        bookingFlightColumns,
        receivableEventColumns,
        batchGenerateColumns,
        fuelCalcColumns,
        fuelCalcTierColumns,
    };
};
